import { CLASS_NAMES, EVENTS } from '@bs/constants';
import { MessageBus } from '@bs/utils';
import SELECTORS from '../../Constants/selectors';

/**
 * A class for the Timeline module
 */
export default class Timeline extends MessageBus {
  /**
   * Constructor function of the Timeline class. Must receive one argument, a DOM
   * node representing the timeline module container.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    this.element = element;
    this.events = [
      ...this.element.querySelectorAll(`.${CLASS_NAMES.TIMELINE_EVENT_CONTENT}`)
    ];
    this.videoEvents = [
      ...this.element.querySelectorAll(`.${CLASS_NAMES.TIMELINE_EVENT_VIDEO}`)
    ];

    this.players = {};

    // Initialize the timeline
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {object} Timeline A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences()
      .setupHandlers()
      .enable()
      .initializeYouTubeVideos();
    return this;
  }

  /**
   * Cache DOM References
   * Find all necessary DOM elements used in the view and cache them
   * @returns {object} Timeline A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.scrollHandler = this.handleScroll.bind(this);
    this.videoClickHandler = this.playVideo.bind(this);
    return this;
  }

  /**
   * Create event handlers to enable interaction with the view
   * @returns {object} Timeline A reference to the current instance of the class
   * @chainable
   */
  enable() {
    window.addEventListener(EVENTS.SCROLL, this.scrollHandler);
    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    return this;
  }

  /**
   * Handles scroll event to check if timeline events are visible in viewport.
   */
  handleScroll() {
    this.events.forEach((event) => {
      if (Timeline.isElementInViewport(event)) {
        event.classList.add(CLASS_NAMES.TIMELINE_EVENT_CONTENT_VISIBLE);
      }
    });
  }

  /**
   * Check if an element is in the viewport (Static Method)
   * @param {HTMLElement} element - The element to check
   * @returns {boolean} True if the element is in the viewport, else false
   */
  static isElementInViewport(element) {
    const rect = element.getBoundingClientRect();
    return rect.top < window.innerHeight * 0.5 && rect.bottom > 0;
  }

  /**
   * Initializes YouTube videos if they exist
   */
  initializeYouTubeVideos() {
    if (this.videoEvents.length === 0) return;

    // Load YouTube API script
    const scriptTag = document.createElement(SELECTORS.SCRIPT);
    scriptTag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(scriptTag);

    window.onYouTubeIframeAPIReady = () => {
      this.videoEvents.forEach((video) => {
        const embed = video.querySelector(
          `.${CLASS_NAMES.TIMELINE_EVENT_VIDEO_EMBED}`
        );
        const videoId = embed.getAttribute(SELECTORS.DATA_YT);
        const uniqueId = `yt-${Math.random().toString(36).substring(2, 6)}`;
        embed.id = uniqueId;

        if (window.YT) {
          this.players[uniqueId] = new window.YT.Player(uniqueId, {
            videoId,
            playerVars: {
              rel: 0,
              showinfo: 0
            }
          });

          const playButton = video.querySelector(
            `.${CLASS_NAMES.TIMELINE_EVENT_VIDEO_PLAY}`
          );
          if (playButton) {
            playButton.addEventListener(EVENTS.CLICK, () =>
              this.playVideo(video, uniqueId)
            );
          }
        }
      });
    };
  }

  /**
   * Handles play button click to start YouTube video
   * @param {HTMLElement} videoElement - The video wrapper element
   * @param {string} videoId - The YouTube video player ID
   */
  playVideo(videoElement, videoId) {
    const thumbnail = videoElement.querySelector(
      `.${CLASS_NAMES.TIMELINE_EVENT_VIDEO_THUMBNAIL}`
    );
    const playButton = videoElement.querySelector(
      `.${CLASS_NAMES.TIMELINE_EVENT_VIDEO_PLAY}`
    );

    if (this.players[videoId]) {
      this.players[videoId].playVideo();
    }

    if (thumbnail) {
      thumbnail.style.transition = 'opacity 0.5s';
      thumbnail.style.opacity = '0';
      setTimeout(() => {
        thumbnail.style.display = 'none';
      }, 500);
    }

    playButton.style.transition =
      'opacity 0.25s ease-out, visibility 0.25s ease-out';
    playButton.style.opacity = '0';
    playButton.style.pointerEvents = 'none';

    playButton.getBoundingClientRect();

    setTimeout(() => {
      playButton.style.display = 'none';
      playButton.style.visibility = 'hidden';
    }, 250);
  }
}
